/**
 * Documentation Page Entity Schemas
 *
 * Customer documentation — platform-global, NOT per-school, exactly like the
 * marketing blog and unlike `Page`. A documentation page carries a globally
 * unique slug holding its FULL reader path, a navigation placement (`section`
 * plus `sectionOrder`), a four-state visibility, and a two-status version model
 * (DRAFT / PUBLISHED). See `apps/backend/prisma/schema/docs.prisma` (U4) for
 * the storage these contracts describe.
 *
 * This file is the ENTITY vocabulary. The BLOCK vocabulary — what may appear
 * inside a page body — lives in `docs-content.ts` and is the security boundary
 * for author-supplied JSON. The two are deliberately separate files: one
 * describes the page, the other describes what a page is made of, and the
 * block vocabulary is shared with the internal runbooks.
 *
 * See docs/plans/2026-08-08-002-feat-content-authoring-strategy-plan.md (U5).
 */

import { z } from 'zod'

// ============================================
// Identity + placement
// ============================================

/**
 * The FULL reader path under /docs (`communications/sending-a-message`), not
 * just the leaf — the schema stores it that way so resolving a request is one
 * lookup on one indexed column and renaming a navigation section cannot break a
 * live URL.
 *
 * Multi-segment on purpose, which is why this is NOT `BlogPostSlugSchema`: blog
 * slugs are single-segment and 64 chars, documentation paths nest and the
 * existing corpus already carries multi-segment ones. 191 matches the column,
 * which is the classic utf8mb4-safe bound for a single-column unique index.
 *
 * A leading or trailing `/`, a doubled `//`, and an empty segment are all
 * rejected: each would produce a second spelling of the same page, and the slug
 * is a unique key.
 */
const SLUG_SEGMENT = '[a-z0-9]+(?:-[a-z0-9]+)*'
const DOC_SLUG_PATTERN = new RegExp(`^${SLUG_SEGMENT}(?:/${SLUG_SEGMENT})*$`)

export const DOC_SLUG_MESSAGE =
  'Slug must be lowercase path segments of ASCII letters, digits, and hyphens, separated by single slashes'

export const DocSlugSchema = z.string().min(1).max(191).regex(DOC_SLUG_PATTERN, DOC_SLUG_MESSAGE)

/**
 * Navigation section key. A plain string, NOT a foreign key — a page whose
 * section no longer exists must SURFACE rather than vanish, so nothing
 * referentially constrains this (see the `section` comment in docs.prisma).
 */
export const DocSectionSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Section must be lowercase ASCII letters, digits, and hyphens')

/** Authored position within a section. Ties break by title at read time. */
export const DocSectionOrderSchema = z.number().int().min(0).max(10_000)

// ============================================
// Visibility + version status
// ============================================

/**
 * Four states (KTD5), matching the `DocVisibility` enum:
 *
 *   - PUBLIC          renders for anyone.
 *   - PRIVATE         a signed-out reader is redirected to login.
 *   - SUPER_ADMIN     returns not-found to everyone else, so its existence is
 *                     not revealed.
 *   - LINK_SHAREABLE  renders for anyone holding the page's share token, and is
 *                     excluded from navigation for EVERY viewer.
 *
 * The read path for LINK_SHAREABLE (token resolution and rotation) is U9. This
 * schema carries the state so the write path can set it; the navigation read
 * omits those pages entirely.
 */
export const DocVisibilitySchema = z.enum(['PUBLIC', 'PRIVATE', 'SUPER_ADMIN', 'LINK_SHAREABLE'])
export type DocVisibility = z.infer<typeof DocVisibilitySchema>

export const DocVersionStatusSchema = z.enum(['DRAFT', 'PUBLISHED'])
export type DocVersionStatus = z.infer<typeof DocVersionStatusSchema>

/**
 * The share token that IS the address of a link-shareable page (U9).
 *
 * Minted from a CSPRNG and never derived from the slug, title, or id — see
 * `apps/backend/src/services/docs/crud/doc-share-token.ts`, the only place in
 * the platform that produces one. This schema is the WIRE shape: what a caller
 * may put in a URL, not what the mint emits.
 *
 * A RANGE rather than the mint's exact length, deliberately. Today's format is
 * 32 CSPRNG bytes rendered base64url (43 characters); pinning 43 here would
 * make widening the format a breaking contract change, while every token minted
 * before it stayed valid. The bound that matters is the LOWER one: it refuses a
 * short guess at the boundary, so nothing that could plausibly be brute-forced
 * ever reaches a database lookup.
 *
 * The character class matches base64url exactly. A token arriving with a `/`,
 * `+`, or `=` in it did not come from this platform, and a percent-encoded path
 * segment is not a shape this endpoint accepts.
 */
export const DOC_SHARE_TOKEN_MESSAGE = 'Share token must be 20-64 base64url characters'

export const DocShareTokenSchema = z
  .string()
  .min(20)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, DOC_SHARE_TOKEN_MESSAGE)

// ============================================
// Doc + DocVersion contracts
// ============================================

export const DocVersionSchema = z.object({
  publicId: z.string(),
  status: DocVersionStatusSchema,
  /**
   * The editor document. Typed as unknown JSON rather than `DocsContentSchema`
   * on the way OUT: a row written before a vocabulary tightening still has to
   * be readable in the editor so an author can fix it. The tightening is
   * enforced on the way IN (draft save) and again before publish, which is
   * where rejecting it protects a reader.
   */
  content: z.unknown(),
  /**
   * Sanitized HTML materialised at publish time. Null on drafts and on any
   * published version stored before the client supplied HTML — consumers fall
   * back to rendering `content` rather than treating null as an error.
   */
  renderedHtml: z.string().nullable(),
  /** Which renderer produced `renderedHtml`; null means unknown provenance, i.e. stale. */
  renderedHtmlVersion: z.string().nullable(),
  createdAt: z.string().datetime(),
  publishedAt: z.string().datetime().nullable()
})
export type DocVersion = z.infer<typeof DocVersionSchema>

/** Fields shared by every representation of a documentation page. */
export const DocBaseSchema = z.object({
  publicId: z.string(),
  slug: DocSlugSchema,
  title: z.string(),
  description: z.string().nullable(),
  visibility: DocVisibilitySchema,
  section: DocSectionSchema,
  sectionOrder: z.number().int()
})
export type DocBase = z.infer<typeof DocBaseSchema>

/** Admin-facing page with both versions resolved. */
export const DocSchema = DocBaseSchema.extend({
  draftVersion: DocVersionSchema.nullable(),
  publishedVersion: DocVersionSchema.nullable(),
  /** True once the page has ever been published — the slug is read-only from then on. */
  slugLocked: z.boolean(),
  /**
   * The live share token, or null on every page that is not link-shareable.
   *
   * On the ADMIN read only, and deliberately not on `DocBaseSchema`: putting it
   * there would carry it into `DocNavigationItemSchema`, which is the one
   * payload a signed-out stranger can fetch in bulk — publishing every share
   * token in the corpus to anyone who asks for the navigation. The author needs
   * it to copy the link; nobody else does.
   */
  shareToken: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime()
})
export type Doc = z.infer<typeof DocSchema>

/**
 * One navigation entry. Carries `searchText` because the docs layout serializes
 * the whole corpus's search text on every render — without it the reader path
 * would fetch every page individually to rebuild the search index.
 *
 * `searchText` is DERIVED at read time from the published version's blocks, not
 * stored (docs.prisma pins that decision on `contentJson`): a second derived
 * cache would need its own staleness marker, and `renderedHtml` already carries
 * the one this domain pays for.
 */
export const DocNavigationItemSchema = DocBaseSchema.extend({
  searchText: z.string(),
  publishedAt: z.string().datetime()
})
export type DocNavigationItem = z.infer<typeof DocNavigationItemSchema>

/**
 * ONE published page, as a reader receives it (U9). The reader route adopts
 * this in U11; the resolution and authorization behind it is U9's.
 *
 * Only ever produced for a page the requester has already been cleared to read
 * — the four-state decision happens in `services/docs/read/resolve-doc-page.ts`
 * BEFORE this shape is built, and a page the viewer may not open is answered
 * with an error rather than a redacted payload. There is nothing to redact
 * here, and no field on it is an authorization input.
 *
 * `shareToken` is deliberately ABSENT even on a link-shareable page: the reader
 * already holds the token (it is how they arrived), and echoing it back would
 * put the secret into every page cache, log line, and copied "view source".
 */
export const DocReaderPageSchema = DocBaseSchema.extend({
  /**
   * Whether the page must tell crawlers not to index it. Computed SERVER-SIDE
   * from the visibility rather than inferred by each client: a link-shareable
   * page whose renderer forgot the directive is a page waiting to be indexed,
   * and "functionally public" is the exact failure the tier exists to avoid.
   *
   * True for every state except PUBLIC. A private or staff-only page is already
   * gated, so the directive is belt-and-braces there; on the link-shareable tier
   * it is the only thing standing between a shared URL and a search result.
   */
  noIndex: z.boolean(),
  /**
   * The published version's block JSON. Typed as unknown for the same reason
   * `DocVersionSchema.content` is: a page stored before a vocabulary tightening
   * must still render rather than fail a parse on the way to a reader.
   */
  content: z.unknown(),
  /** Sanitized HTML baked at publish time; null means "render `content` instead", never an error. */
  renderedHtml: z.string().nullable(),
  /** Which renderer produced `renderedHtml`; null means unknown provenance, i.e. stale. */
  renderedHtmlVersion: z.string().nullable(),
  publishedAt: z.string().datetime()
})
export type DocReaderPage = z.infer<typeof DocReaderPageSchema>
